import { useEffect, useRef } from 'react';

// Matches the theme's --primary (green) and --background (near-black green).
const NET_COLOR = 0x21c45d;
const BACKGROUND_COLOR = 0x0a100e;

let vendorScriptsPromise: Promise<void> | null = null;

// three.js + vanta.net are ~620KB combined — loading them as blocking <script>
// tags delayed first paint by seconds. Fetch them lazily, off the critical
// rendering path, and only once (cached) across mounts.
function loadVendorScripts(): Promise<void> {
  if (vendorScriptsPromise) return vendorScriptsPromise;

  const loadScript = (src: string) =>
    new Promise<void>((resolve, reject) => {
      const script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error(`Failed to load ${src}`));
      document.body.appendChild(script);
    });

  vendorScriptsPromise = loadScript('/vendor/three.r134.min.js').then(() =>
    loadScript('/vendor/vanta.net.min.js'),
  );
  return vendorScriptsPromise;
}

const AnimatedBackground = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const effectRef = useRef<ReturnType<NonNullable<Window['VANTA']>['NET']> | null>(null);

  useEffect(() => {
    // The WebGL net redraws every frame indefinitely — on phone-class CPUs
    // this dominates the main thread. Keep it desktop-only, and skip for
    // anyone who's asked for reduced motion.
    const isDesktop = window.matchMedia('(min-width: 768px)').matches;
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!isDesktop || prefersReducedMotion || !containerRef.current) return;

    let cancelled = false;

    loadVendorScripts().then(() => {
      if (cancelled || !containerRef.current || !window.VANTA) return;
      effectRef.current = window.VANTA.NET({
        el: containerRef.current,
        mouseControls: true,
        touchControls: false,
        gyroControls: false,
        minHeight: 200.0,
        minWidth: 200.0,
        scale: 1.0,
        scaleMobile: 1.0,
        color: NET_COLOR,
        backgroundColor: BACKGROUND_COLOR,
        points: 8,
        maxDistance: 18,
        spacing: 18,
      });
    });

    return () => {
      cancelled = true;
      effectRef.current?.destroy();
      effectRef.current = null;
    };
  }, []);

  return <div ref={containerRef} className="fixed inset-0 -z-20" aria-hidden="true" />;
};

export default AnimatedBackground;
